import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pagination } from '@liveconnect/components'

import { Main } from '../../components/Main'
import { TenantUser } from '../../core/tenantUsers/types'
import CreateTenantUser from './CreateTenantUser'
import useNotifications from '../../utils/notifications/useNotifications'
import useUi from '../../core/ui/useUi'
import { Profile } from '../../core/profiles/types'
import ProfilesModal from './ProfilesModal'
import useTenantUsers from '../../core/tenantUsers/useTenantUsers'
import useAuth from '../../core/auth/useAuth'

import './styles.scss'
import { ColumnDef, Row } from '@tanstack/react-table'
import ContentTable from '../../components/ContentTable'

const TenantUsersScreen: FC = () => {
  const { t } = useTranslation()
  const notify = useNotifications()
  const {
    list,
    search,
    isLoaded,
    availableItems,
    page,
    availablePages,
    pageSize,
    fetchList,
    clearList,
    deleteUser,
  } = useTenantUsers()
  const { showConfirmation } = useUi()
  const { user } = useAuth()
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [showProfiles, setShowProfiles] = useState<Profile[] | null>(null)
  const [edittingUser, setEdittingUser] = useState<TenantUser>()

  const handleDelete = async (id: string) => {
    try {
      await deleteUser(id)
      notify.success(t('tenantUser.delete.successFeedback'))
      fetchList({ page, search })
    } catch (e) {}
  }

  const handleConfirmDelete = (id: string) => {
    showConfirmation({
      title: t('tenantUser.delete.modal.title'),
      subtitle: t('tenantUser.delete.modal.subtitle'),
      iconName: 'report_problem',
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
      onConfirm: () => {
        handleDelete(id)
      },
    })
  }

  const handleCloseForm = () => {
    setShowCreateModal(false)
    setEdittingUser(undefined)
  }

  const actions = (row: TenantUser) => {
    return [
      {
        onClick: () => setEdittingUser(row),
        label: t('tenantUser.list.table.action.edit'),
        icon: 'edit',
        isHidden: row.id === user?.profile.sub,
      },
      {
        onClick: () => handleConfirmDelete(row.id),
        label: t('tenantUser.list.table.action.delete'),
        icon: 'delete_outline',
        isHidden: row.id === user?.profile.sub,
      },
    ]
  }

  const columns: ColumnDef<TenantUser>[] = useMemo(
    () => [
      {
        accessorKey: 'email',
        header: t('tenantUser.list.table.email'),
      },
      {
        accessorKey: 'name',
        header: t('tenantUser.list.table.name'),
        cell: ({ row }: { row: Row<TenantUser> }) =>
          `${row.original.name} ${row.original.surname}`,
      },
      {
        accessorKey: 'profiles',
        header: t('tenantUser.list.table.profiles'),
        cell: ({ row }: { row: Row<TenantUser> }) =>
          renderProfilesCell(row.original),
      },
    ],
    []
  )

  useEffect(() => {
    fetchList({ page: 1 })
    return () => {
      clearList()
    }
  }, [])

  const renderProfilesCell = (row: TenantUser) => {
    if (!row.profiles?.length) return ''
    return row.profiles.length === 1 ? (
      row.profiles[0].name
    ) : (
      <div
        className="Tenant__profiles"
        onClick={() => setShowProfiles(row.profiles)}
      >
        <div className="Tenant__profiles__cell">{`${row.profiles.length} ${t(
          'tenantUser.list.table.profiles'
        )}`}</div>
      </div>
    )
  }

  return (
    <>
      <Main ariaLabelledby="user-list-title" className="Tenant">
        <div className="d-flex justify-content-between">
          <div>
            <h1 id="user-list-title" className="h3">
              {t('tenantUser.title')}
            </h1>
            <p>{t('tenantUser.list.hint')}</p>
          </div>
          <button
            className="btn btn-primary h-100"
            onClick={() => setShowCreateModal(true)}
          >
            {t('tenantUser.list.add')}
          </button>
        </div>
        <section className="mt-3">
          <div className="Tenant__list">
            <ContentTable
              title={t('admin.table.title')}
              columns={columns}
              data={list}
              emptyText={t('tenantUser.emptyList')}
              actions={actions}
              onSearch={(s: string) => {
                fetchList({ page: 1, search: s })
              }}
              isLoaded={isLoaded}
              searchPlaceholder={t('tenantUser.list.search')}
            />
            <Pagination
              currentPage={(page || 1) - 1}
              totalPages={availablePages}
              totalItems={availableItems}
              pageSize={pageSize}
              singlePageText={(count) =>
                t('common.pager.singlePage', { count })
              }
              multiplePageText={(start, end, total) =>
                t('common.pager.multiplePage', { start, end, total })
              }
              onChange={(pageIndex) => fetchList({ page: pageIndex + 1 })}
            />
          </div>
        </section>
      </Main>
      {(showCreateModal || edittingUser) && (
        <CreateTenantUser onClose={handleCloseForm} user={edittingUser} />
      )}
      {showProfiles && (
        <ProfilesModal
          onClose={() => setShowProfiles(null)}
          profiles={showProfiles}
        />
      )}
    </>
  )
}

export default TenantUsersScreen
